import { AffiliationAvailabilityBoundary } from '@/hooks/use-affiliation-availability';
import { Text, VStack } from '@chakra-ui/react';
import {
  ProgramStatus,
  type Creator,
  type CreatorUgcOffer,
  type WishlistCreator,
} from '@fluenceur/types';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import ModalShell, { type ModalShellStep } from '@/components/ui/modal-shell';
import { LoadingState } from '@/components/ui/loading-state';
import {
  InvitationProvider,
  useInvitationActions,
  useInvitationData,
} from '@/contexts/invitation-context';
import { useFetchInvitablePrograms } from '@/hooks/queries/use-fetch-invitable-programs';
import { useListProgram } from '@/hooks/queries/use-list-program';
import type { InvitableProgramResponse } from '@/types/affiliate-programs';
import type {
  CollabBriefMode,
  CollabReseedSeed,
  InvitationChannelMode,
  InvitationEntryIntent,
  InvitationView,
} from '@/stores/invitation-store';
import type { CollabType, CollabWizardType } from './collab-types';
import CollabBriefStep from './collab/collab-brief-step';
import CollabDeliveryStep from './collab/collab-delivery-step';
import CollabDoneStep from './collab/collab-done-step';
import CollabDraftStep from './collab/collab-draft-step';
import CollabSendStep from './collab/collab-send-step';
import CollabTermsStep from './collab/collab-terms-step';
import BulkProgressView from './bulk-progress-view';
import InvitationChannelStep from './invitation-channel-step';
import InvitationDetailsView from './invitation-details-view';
import ProgramSelectionView from './program-selection-view';
import { InputSelectionView } from './input-selection-view';
import TypeSelectionView from './type-selection-view';

/**
 * How the modal was opened. It fixes the step path (and therefore the progress
 * dots) up front, so the count never shifts as the user advances and `target`
 * fills in mid-flow. The propose-collab path additionally forks on the chosen
 * collaboration type (and brief mode), which is why its path is derived rather
 * than static.
 */
export type EntryMode = 'propose-collab' | 'targeted' | 'smart-input';

/**
 * Linear step path per entry mode; drives the progress dots and back gating.
 * These are the MONO paths (they include the channel step); the bulk wishlist
 * flow drops that step (see `getStepPath`).
 */
const STEP_PATHS: Record<Exclude<EntryMode, 'propose-collab'>, InvitationView[]> = {
  targeted: ['programSelection', 'invitationChannel', 'invitationDetails'],
  'smart-input': ['inputSelection', 'programSelection', 'invitationChannel', 'invitationDetails'],
};

const AFFILIATION_PATH: InvitationView[] = [
  'typeSelection',
  'programSelection',
  'invitationChannel',
  'invitationDetails',
];
const SPONSORED_TEMPLATE_PATH: InvitationView[] = [
  'typeSelection',
  'collabBrief',
  'collabTerms',
  'collabSend',
  'collabDelivery',
];
const SPONSORED_BLANK_PATH: InvitationView[] = [
  'typeSelection',
  'collabBrief',
  'collabDraft',
  'collabTerms',
  'collabSend',
  'collabDelivery',
];

/**
 * A collab reseed (drawer: repropose / correct-and-resend / manual resend)
 * opens directly on pre-filled terms: terms, channel and delivery only, no Back
 * below the first. The earlier wizard steps were never walked, so exposing them
 * through Back would let a stray "Continue" on the brief step wipe the
 * pre-filled content.
 */
const RESEED_PATH: InvitationView[] = ['collabTerms', 'collabSend', 'collabDelivery'];

/**
 * The active step path. Propose-collab starts on the affiliation-length path
 * (3 dots before any choice, per the design) and forks once Sponsored post is
 * picked: 5 steps from a template, 6 through the blank draft. A reseed entry
 * uses its own short path. The affiliation channel step is on the path for both
 * mono AND bulk (symmetry). The done/progress epilogues are not steps — they
 * fill every dot.
 */
export const getStepPath = (
  entryMode: EntryMode,
  collabType: CollabType | null,
  briefMode: CollabBriefMode | null,
  isReseed: boolean
): InvitationView[] => {
  if (isReseed) return RESEED_PATH;
  return entryMode !== 'propose-collab'
    ? STEP_PATHS[entryMode]
    : collabType === 'sponsored' || collabType === 'ugc'
      ? briefMode === 'blank'
        ? SPONSORED_BLANK_PATH
        : SPONSORED_TEMPLATE_PATH
      : AFFILIATION_PATH;
};

/** Mode-aware subtitle of the mono delivery step, echoing the channel picked. */
const CHANNEL_DETAIL_SUBTITLE: Record<InvitationChannelMode, string> = {
  email: 'invitations.modal.channel.deliverySubtitle.email',
  dm: 'invitations.modal.channel.deliverySubtitle.dm',
  comment: 'invitations.modal.channel.deliverySubtitle.comment',
  link: 'invitations.modal.channel.deliverySubtitle.link',
};

const SUBTITLE_KEYS: Record<InvitationView, string> = {
  typeSelection: 'invitations.modal.collab.subtitles.type',
  programSelection: 'invitations.modal.collab.subtitles.program',
  inputSelection: 'invitations.modal.collab.subtitles.input',
  invitationChannel: 'invitations.modal.channel.subtitle',
  // Type-completeness fallback only: the mono delivery step always receives its
  // mode-aware override (computed in the modal body); bulk keeps this subtitle.
  invitationDetails: 'invitations.modal.collab.subtitles.details',
  collabBrief: 'invitations.modal.collab.subtitles.brief',
  collabDraft: 'invitations.modal.collab.subtitles.draft',
  collabTerms: 'invitations.modal.collab.subtitles.terms',
  collabSend: 'invitations.modal.collab.subtitles.send',
  // Type-completeness fallback only: the delivery step always receives its
  // mode-aware override (computed in the modal body), so this variable-free
  // channel subtitle should never actually render.
  collabDelivery: 'invitations.modal.collab.subtitles.send',
  collabDone: 'invitations.modal.collab.subtitles.done',
  bulkProgress: 'invitations.modal.channel.bulk.progressSubtitle',
};

/**
 * Views that never rewind: the channel step and the affiliation details step
 * (the invitation is created before both; a Back to program selection would
 * re-create one), and the wizard's done screen (the collabs are sent). The
 * channel ← details return goes through the banner's "Change", not the header.
 */
const NO_BACK_VIEWS: ReadonlySet<InvitationView> = new Set([
  'invitationChannel',
  'invitationDetails',
  'collabDone',
  'bulkProgress',
]);

const getStepMeta = (currentView: InvitationView, path: InvitationView[]) => {
  // The done / bulk-progress epilogues fill every dot (not steps themselves).
  if (currentView === 'collabDone' || currentView === 'bulkProgress') {
    return { count: path.length, current: path.length - 1 };
  }
  const index = path.indexOf(currentView);
  return { count: path.length, current: index < 0 ? 0 : index };
};

interface InvitationTargetProps {
  creator?: Creator; // Make creator optional for the new flow
  wishlistItems?: WishlistCreator[]; // For bulk wishlist invitations
  sourceRecommendationId?: string;
  /**
   * Brand-known email for the creator (propose-collab entry). When present, the
   * invitation-details step becomes the EMAIL channel instead of DM/comment.
   */
  recipientEmail?: string | null;
  /** Opens on the collaboration-type step (contact card / wishlist "propose"). */
  initialView?: Extract<InvitationView, 'typeSelection'>;
  /**
   * Program the caller is already scoped to (the program-scoped invitations
   * page). Arms the program step on that program so the brand does not re-pick
   * the program it is looking at. It is a default, not a lock: the step still
   * renders the full list and the brand can switch.
   */
  preselectedProgramId?: string;
  /** Resolved identity of the mono recipient (contact card propose entry). */
  creatorIdentityId?: string | null;
  /**
   * Recipient display for a propose-collab contact with no social profile (no
   * Creator object): seeds the sponsored wizard's recipient line. Ignored when a
   * `creator` is present.
   */
  recipientName?: string | null;
  recipientAvatarUrl?: string | null;
  /**
   * "Resend from an existing collab" entry (drawer): opens the sponsored wizard
   * pre-filled on the terms step. Additive — every other entry omits it.
   */
  seedCollab?: CollabReseedSeed | null;
  /**
   * Library brief the propose-collab entry starts from (brief editor "Send to a
   * creator"): the wizard opens on the sponsored brief step with it selected.
   * Only read with `initialView="typeSelection"`.
   */
  initialBriefTemplateId?: string;
  /**
   * The wizard path the brief step opens on: the type of the brief named above,
   * or UGC from the contact sheet's UGC row. Only read with
   * `initialView="typeSelection"`.
   */
  initialCollabType?: CollabWizardType;
  /** The mono recipient's UGC offer, while her badge is active. */
  recipientUgcOffer?: CreatorUgcOffer | null;
  /**
   * The brief application the proposal answers (Candidatures page). Sent with
   * the proposal so the application turns "proposed" when it leaves.
   */
  briefApplicationId?: string;
  entryIntent?: InvitationEntryIntent;
}

export type SendInvitationModalProps = {
  isOpen: boolean;
  onClose: ({ success }: { success?: boolean }) => void;
} & InvitationTargetProps;

export const buildShellStep = (
  currentView: InvitationView,
  entryMode: EntryMode,
  path: InvitationView[],
  creator: Creator | null,
  bulkCount: number,
  // Who the collab is being proposed to (creator name, or the resolved recipient
  // display for a contact with no Creator object). Names the propose-collab
  // header; ignored by the plain invitation entries.
  recipientName: string | null,
  t: TFunction,
  onBack: (previous: InvitationView) => void,
  // Mode-aware subtitle for the delivery step (the static map covers the rest).
  subtitleOverride?: string
): ModalShellStep => {
  const creatorName = creator?.name;
  const title =
    entryMode === 'propose-collab'
      ? bulkCount > 1
        ? t('invitations.modal.collab.proposeTitleBulk', { count: bulkCount })
        : recipientName
          ? t('invitations.modal.collab.proposeTitleWithCreator', { creatorName: recipientName })
          : t('invitations.modal.collab.proposeTitle')
      : creatorName
        ? t('invitations.modal.titleWithCreator', { creatorName })
        : t('invitations.modal.title');

  // Back rewinds one step in the active path. First steps and terminal views
  // (details: invitation already created; done: collabs already sent) have none.
  const index = path.indexOf(currentView);
  const showBack = index > 0 && !NO_BACK_VIEWS.has(currentView);
  const previous = showBack ? path[index - 1] : null;
  const { count, current } = getStepMeta(currentView, path);

  return {
    title,
    subtitle: subtitleOverride ?? t(SUBTITLE_KEYS[currentView]),
    showBack,
    onBack: previous ? () => onBack(previous) : undefined,
    dots: { count, current },
  };
};

const InvitationModalContent = ({
  isOpen,
  onClose,
  entryMode,
  preselectedProgramId,
}: {
  isOpen: boolean;
  onClose: ({ success }: { success?: boolean }) => void;
  entryMode: EntryMode;
  preselectedProgramId?: string;
}) => {
  const { t } = useTranslation();
  const { setPrograms, setCurrentView, setSelectedProgram } = useInvitationActions();
  const {
    loading,
    error,
    invitationData,
    currentView,
    creator,
    target,
    selectedProgram,
    collabType,
    collabBriefMode,
    collabDeliveryMode,
    invitationChannelMode,
    collabReseedRecipient,
    collabRecipientDisplay,
    entryIntent,
  } = useInvitationData();

  // Targets that carry no Creator entity (bulk wishlist, raw email, existing
  // affiliate) resolve their programs from the active-programs list: the
  // per-creator invitable endpoint keys off a creator id they do not have.
  const hasNoCreatorEntity =
    target?.type === 'wishlist-bulk' || target?.type === 'email' || target?.type === 'affiliate';

  // For creator-based invitations, use the invitable programs endpoint
  const entityId = creator?.id || (target?.type === 'creator' ? target.data.id : '');
  const shouldFetchInvitablePrograms = !!entityId && currentView === 'programSelection';

  // For bulk/email/affiliate, use the list programs endpoint with status filter
  const shouldFetchAllPrograms = hasNoCreatorEntity && currentView === 'programSelection';

  // Fetch invitable programs for creator-based flow
  const {
    data: invitablePrograms,
    isLoading: invitableProgramsLoading,
    error: invitableProgramsError,
  } = useFetchInvitablePrograms(entityId, { enabled: shouldFetchInvitablePrograms });

  // Fetch all active programs for bulk/email flow
  const {
    data: allPrograms,
    isLoading: allProgramsLoading,
    error: allProgramsError,
  } = useListProgram({ status: ProgramStatus.Active }, { enabled: shouldFetchAllPrograms });

  // Transform all programs to InvitableProgramResponse format
  const transformedPrograms = useMemo<InvitableProgramResponse[]>(() => {
    if (!allPrograms) return [];
    return allPrograms.map((program) => ({
      id: program.id,
      name: program.name,
      description: program.description ?? null,
      brandId: program.brandId,
      canInvite: true, // All active programs are invitable for bulk
      isExpired: false, // Active programs are not expired
      lastInvitationDate: null as unknown as Date,
      commissionSettings: program.commissionSettings,
      discountSettings: program.discountSettings,
    }));
  }, [allPrograms]);

  // Combine loading and error states
  const programsLoading = shouldFetchInvitablePrograms
    ? invitableProgramsLoading
    : allProgramsLoading;
  const programsError = shouldFetchInvitablePrograms ? invitableProgramsError : allProgramsError;
  const programs = shouldFetchInvitablePrograms ? invitablePrograms : transformedPrograms;

  // Sync programs from React Query to Zustand store
  // Include currentView as dependency to re-sync after store reset
  useEffect(() => {
    if (programs && currentView === 'programSelection') {
      setPrograms(programs);
    }
  }, [programs, setPrograms, currentView]);

  // Arm the program step on the caller's program (program-scoped entry).
  // Resolved against the LOADED list rather than seeded blind: an id that is
  // absent or not invitable leaves the step untouched, so the primary action is
  // never armed on a program the brand cannot see in the list. Skipped once a
  // choice exists, so a manual switch is never overwritten.
  useEffect(() => {
    if (!preselectedProgramId || currentView !== 'programSelection' || selectedProgram?.id) return;
    const match = programs?.find(
      (program) => program.id === preselectedProgramId && program.canInvite
    );
    if (match) setSelectedProgram(match);
  }, [preselectedProgramId, programs, currentView, selectedProgram?.id, setSelectedProgram]);

  const isBulk = target?.type === 'wishlist-bulk';
  const path = getStepPath(entryMode, collabType, collabBriefMode, !!collabReseedRecipient);
  const bulkCount = isBulk ? target.data.length : 0;
  // Name the propose-collab header after whoever it targets: the Creator when one
  // is present, else the resolved contact display (contact card / picker), else
  // the reseeded recipient (drawer). Plain invitation entries pass null → title
  // unchanged.
  const recipientName =
    creator?.name ??
    collabRecipientDisplay?.displayName ??
    collabReseedRecipient?.displayName ??
    null;
  // Delivery-step subtitle follows the mode picked on the channel step: compose
  // for email, conversation for DM, hand-the-link (mono/bulk) otherwise.
  const deliverySubtitle =
    currentView === 'collabDelivery'
      ? collabDeliveryMode === 'email'
        ? t('invitations.modal.collab.subtitles.deliveryEmail')
        : collabDeliveryMode === 'dm'
          ? t('invitations.modal.collab.subtitles.deliveryDm')
          : bulkCount > 1
            ? t('invitations.modal.collab.subtitles.deliveryLinkBulk')
            : t('invitations.modal.collab.subtitles.deliveryLink', {
                name: recipientName ?? '',
              })
      : undefined;
  // Mono delivery step: the subtitle echoes the channel picked on the channel
  // step. Bulk keeps the static `details` subtitle from SUBTITLE_KEYS.
  const detailsSubtitle =
    currentView === 'invitationDetails' && !isBulk
      ? t(CHANNEL_DETAIL_SUBTITLE[invitationChannelMode ?? 'link'])
      : undefined;
  const step = buildShellStep(
    currentView,
    entryMode,
    path,
    creator,
    bulkCount,
    recipientName,
    t,
    (previous) => setCurrentView(previous),
    deliverySubtitle ?? detailsSubtitle
  );
  const shellStep =
    entryIntent === 'affiliate-add' ? { ...step, title: t('affiliates.addNew.title') } : step;

  const renderBody = () => {
    if (currentView === 'typeSelection') {
      return <TypeSelectionView onClose={() => onClose({ success: false })} />;
    }

    if (currentView === 'inputSelection') {
      return <InputSelectionView />;
    }

    if (currentView === 'collabBrief') {
      return <CollabBriefStep onClose={() => onClose({ success: false })} />;
    }
    if (currentView === 'collabDraft') {
      return <CollabDraftStep onClose={() => onClose({ success: false })} />;
    }
    if (currentView === 'collabTerms') {
      return <CollabTermsStep onClose={() => onClose({ success: false })} />;
    }
    if (currentView === 'collabSend') {
      return <CollabSendStep onClose={() => onClose({ success: false })} />;
    }
    if (currentView === 'collabDelivery') {
      return <CollabDeliveryStep onClose={() => onClose({ success: false })} />;
    }
    if (currentView === 'collabDone') {
      return <CollabDoneStep onClose={() => onClose({ success: true })} />;
    }
    if (currentView === 'bulkProgress') {
      return <BulkProgressView onClose={onClose} />;
    }

    if (currentView === 'programSelection' && programsLoading) {
      return <LoadingState message={t('invitations.modal.loading')} />;
    }

    if (programsError) {
      return (
        <Text color="red.500">
          {t('invitations.modal.errorLoadingPrograms', { message: programsError.message })}
        </Text>
      );
    }

    if (currentView === 'programSelection' && !target) {
      return <Text color="orange.500">{t('invitations.modal.noTargetSelected')}</Text>;
    }

    if (currentView === 'programSelection' && !loading && !error) {
      return <ProgramSelectionView onClose={() => onClose({ success: false })} />;
    }

    if (currentView === 'invitationChannel' && invitationData && !loading && !error) {
      return <InvitationChannelStep onClose={onClose} />;
    }

    if (currentView === 'invitationDetails' && invitationData && !loading && !error) {
      // Channel routing now lives in one place — InvitationDetailsView routes the
      // bulk branch, then the mono modes (email/dm/comment/link) off the store's
      // channel mode. No email fork here anymore.
      return <InvitationDetailsView onClose={onClose} />;
    }

    return null;
  };

  const affiliationFlow = entryMode !== 'propose-collab' || collabType === 'affiliation';
  const shell = (
    <ModalShell isOpen={isOpen} onClose={() => onClose({ success: false })} step={shellStep}>
      <VStack w="full" align="stretch" gap={6}>
        {renderBody()}
      </VStack>
    </ModalShell>
  );
  return affiliationFlow ? (
    <AffiliationAvailabilityBoundary isOpen={isOpen} onClose={() => onClose({ success: false })}>
      {shell}
    </AffiliationAvailabilityBoundary>
  ) : (
    shell
  );
};

InvitationModalContent.displayName = 'InvitationModalContent';

const SendInvitationModal = memo<SendInvitationModalProps>(
  ({
    isOpen,
    onClose,
    creator,
    wishlistItems,
    sourceRecommendationId,
    recipientEmail,
    initialView,
    creatorIdentityId,
    recipientName,
    recipientAvatarUrl,
    seedCollab,
    preselectedProgramId,
    initialBriefTemplateId,
    initialCollabType,
    recipientUgcOffer,
    briefApplicationId,
    entryIntent,
  }) => {
    // A collab-reseed entry is a propose-collab flow that opens directly on the
    // sponsored terms step, so it shares the propose-collab step path.
    const entryMode: EntryMode =
      initialView === 'typeSelection' || seedCollab
        ? 'propose-collab'
        : creator || recipientEmail || (wishlistItems && wishlistItems.length > 0)
          ? 'targeted'
          : 'smart-input';

    return (
      <InvitationProvider
        isOpen={isOpen}
        creator={creator}
        wishlistItems={wishlistItems}
        sourceRecommendationId={sourceRecommendationId}
        recipientEmail={recipientEmail}
        initialView={initialView}
        creatorIdentityId={creatorIdentityId}
        recipientName={recipientName}
        recipientAvatarUrl={recipientAvatarUrl}
        seedCollab={seedCollab}
        initialBriefTemplateId={initialBriefTemplateId}
        initialCollabType={initialCollabType}
        recipientUgcOffer={recipientUgcOffer}
        briefApplicationId={briefApplicationId}
        entryIntent={entryIntent}
      >
        <InvitationModalContent
          isOpen={isOpen}
          onClose={onClose}
          entryMode={entryMode}
          preselectedProgramId={preselectedProgramId}
        />
      </InvitationProvider>
    );
  }
);

SendInvitationModal.displayName = 'SendInvitationModal';

export default SendInvitationModal;
